'use client';

import React, { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';

import { NextUI } from '@/components/NextUI';
import { useAppDispatch, useAppSelector } from '@/hooks/redux';
import { store } from '@/store';
import { hideModal } from '@/store/slices/modalSlice';

import {
  COMMON_REVIEW_EVENTS,
  REVIEW_EVENTS,
  logCommonReviewEvent,
  logReviewEvent,
} from '../analytics/events';
import {
  NOT_GOOD_ILLUSTRATION,
  REVIEW_PANEL_GRADIENT_BG,
  REVIEW_COPY,
  REVIEW_ILLUSTRATION,
  THANKYOU_ILLUSTRATION,
} from '../copy';
import { getFeatureText } from '../data/featureText';
import {
  persistCompleted,
  persistSentiment,
  persistShown,
  saveInternalFeedback,
} from '../services/reviewFlowService';
import { markCompleted, markShown, setSentiment } from '../store/reviewSlice';
import type { ReviewSentiment } from '../types';
import { openTrustpilotReview } from '../utils/openTrustpilotReview';

type Step = 'entry' | 'trustpilot' | 'feedback' | 'thankyou';

const ENTRY_OPTIONS: {
  value: ReviewSentiment;
  emoji: string;
  label: string;
}[] = [
  { value: 'great', emoji: '😍', label: REVIEW_COPY.entry.great },
  { value: 'okay', emoji: '😐', label: REVIEW_COPY.entry.okay },
  { value: 'not_good', emoji: '🙁', label: REVIEW_COPY.entry.notGood },
];

const REVIEW_ACTION_BY_SENTIMENT: Record<
  ReviewSentiment,
  'like' | 'dislike' | 'neutral'
> = {
  great: 'like',
  okay: 'neutral',
  not_good: 'dislike',
};

const headingCls =
  'font-barlow text-[20px] font-semibold leading-normal text-[#292a33]';
const subtitleCls =
  'mx-auto w-[300px] max-w-full text-center font-barlow text-[15px] font-medium leading-normal text-black/50';
const primaryBtnCls =
  'flex h-12 w-full items-center justify-center rounded-[5px] bg-[#4b7eff] font-barlow text-[17px] font-medium text-white transition hover:opacity-90 disabled:bg-[#4b7eff]/50 disabled:opacity-100';
const secondaryBtnCls =
  'flex h-12 w-full items-center justify-center rounded-[5px] bg-black/10 font-barlow text-[17px] font-medium text-[#454655] transition hover:bg-black/15';
const trustpilotHeartCls =
  'absolute left-[-35px] top-[-20px] z-10 w-[55%] -rotate-[18deg]';
const trustpilotSmileCls =
  'absolute left-[190px] top-[200px] z-10 w-[50%] rotate-[10deg]';
const thankYouHeartCls =
  'absolute -top-[%] -left-[4%] -top-[4%]  w-[100%] -rotate-[9deg]';
const thankYouSmileCls = 'absolute left-[24%] top-[35%] w-[82%] rotate-[8deg]';
const layerCls = 'absolute inset-0 transition-opacity duration-200';

const ReviewFlowModal = () => {
  const dispatch = useAppDispatch();

  const modalState = useAppSelector((s) => s.modal);
  const uid = useAppSelector((s) => s.auth.user?.uid);
  const trustpilotUrl = useAppSelector((s) => s.review.trustpilotUrl);

  const activeModal = modalState.activeModal;
  const modalData = activeModal
    ? modalState.modals[activeModal]?.modalData
    : null;
  const featureName: string | null = modalData?.featureName ?? null;
  const featureText = getFeatureText(featureName);

  const [step, setStep] = useState<Step>('entry');
  const [sentiment, setSentimentState] = useState<ReviewSentiment | null>(null);
  const [feedbackText, setFeedbackText] = useState('');
  const [awaitingReview, setAwaitingReview] = useState(false);

  const finalizedRef = useRef(false);
  const shownRef = useRef(false);
  const popupPollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(
    () => () => {
      if (popupPollRef.current) clearInterval(popupPollRef.current);
    },
    []
  );

  const illustrationKey: keyof typeof REVIEW_ILLUSTRATION =
    step === 'trustpilot'
      ? 'trustpilot'
      : step === 'feedback'
        ? sentiment === 'not_good'
          ? 'feedbackNotGood'
          : 'feedback'
        : step === 'thankyou'
          ? 'thankyou'
          : 'entry';
  const panelBg = REVIEW_ILLUSTRATION[illustrationKey].bg;

  useEffect(() => {
    if (shownRef.current) return;
    shownRef.current = true;

    dispatch(markShown({ now: Date.now() }));
    if (uid) {
      const updated = store.getState().review;
      void persistShown(uid, updated.shownCount, updated.lastShownAt);
    }

    logReviewEvent(REVIEW_EVENTS.FLOW_SHOWN, {
      feature_name: featureName ?? undefined,
    });
    logCommonReviewEvent(COMMON_REVIEW_EVENTS.LND_REVIEW_US, {
      feature_name: featureName ?? undefined,
    });
  }, [featureName, uid, dispatch]);

  useEffect(() => {
    if (step === 'trustpilot') {
      logReviewEvent(REVIEW_EVENTS.TRUSTPILOT_PROMPT_SHOWN, {
        feature_name: featureName ?? undefined,
        sentiment: sentiment ?? undefined,
      });
    } else if (step === 'feedback') {
      logReviewEvent(REVIEW_EVENTS.INTERNAL_FEEDBACK_PROMPT_SHOWN, {
        feature_name: featureName ?? undefined,
        sentiment: sentiment ?? undefined,
      });
    } else if (step === 'thankyou') {
      logReviewEvent(REVIEW_EVENTS.THANK_YOU_SHOWN, {
        feature_name: featureName ?? undefined,
        sentiment: sentiment ?? undefined,
      });
    }
  }, [step, featureName, sentiment]);

  const handleSentiment = (value: ReviewSentiment) => {
    setSentimentState(value);
    dispatch(setSentiment(value));
    if (uid) void persistSentiment(uid, value);
    logReviewEvent(REVIEW_EVENTS.SENTIMENT_SELECTED, {
      feature_name: featureName ?? undefined,
      sentiment: value,
    });
    logCommonReviewEvent(COMMON_REVIEW_EVENTS.BTN_REVIEW_US, {
      feature_name: featureName ?? undefined,
      sentiment: value,
      action: REVIEW_ACTION_BY_SENTIMENT[value],
    });
    setStep(value === 'great' ? 'trustpilot' : 'feedback');
  };

  const handleLeaveReview = () => {
    finalizedRef.current = true;
    logReviewEvent(REVIEW_EVENTS.TRUSTPILOT_REVIEW, {
      feature_name: featureName ?? undefined,
      sentiment: sentiment ?? undefined,
    });

    const popup = openTrustpilotReview(trustpilotUrl);
    dispatch(markCompleted());
    if (uid) void persistCompleted(uid);

    if (!popup) {
      setStep('thankyou');
      return;
    }

    setAwaitingReview(true);
    if (popupPollRef.current) clearInterval(popupPollRef.current);
    popupPollRef.current = setInterval(() => {
      if (popup.closed) {
        if (popupPollRef.current) clearInterval(popupPollRef.current);
        popupPollRef.current = null;
        setAwaitingReview(false);
        setStep('thankyou');
      }
    }, 500);
  };

  const handleTrustpilotLater = () => {
    finalizedRef.current = true;
    logReviewEvent(REVIEW_EVENTS.TRUSTPILOT_DISMISSED, {
      feature_name: featureName ?? undefined,
      sentiment: sentiment ?? undefined,
    });
    dispatch(hideModal());
  };

  const handleShareFeedback = () => {
    if (!feedbackText.trim()) return;
    finalizedRef.current = true;
    logReviewEvent(REVIEW_EVENTS.INTERNAL_FEEDBACK_SUBMITTED, {
      feature_name: featureName ?? undefined,
      sentiment: sentiment ?? undefined,
      feedback_text: feedbackText,
    });
    if (uid) {
      void saveInternalFeedback(uid, {
        sentiment: sentiment ?? 'okay',
        feedbackText,
        featureName,
      });
    }
    setStep('thankyou');
  };

  const handleSkipFeedback = () => {
    finalizedRef.current = true;
    logReviewEvent(REVIEW_EVENTS.INTERNAL_FEEDBACK_SKIPPED, {
      feature_name: featureName ?? undefined,
      sentiment: sentiment ?? undefined,
    });
    dispatch(hideModal());
  };

  const handleContinue = () => {
    finalizedRef.current = true;
    dispatch(hideModal());
  };

  const handleHerouiClose = () => {
    if (finalizedRef.current) return;
    finalizedRef.current = true;
    if (step === 'trustpilot') {
      logReviewEvent(REVIEW_EVENTS.TRUSTPILOT_DISMISSED, {
        feature_name: featureName ?? undefined,
        sentiment: sentiment ?? undefined,
      });
    } else if (step === 'feedback') {
      logReviewEvent(REVIEW_EVENTS.INTERNAL_FEEDBACK_SKIPPED, {
        feature_name: featureName ?? undefined,
        sentiment: sentiment ?? undefined,
      });
    }
  };

  return (
    <NextUI.Modal
      className="max-w-[728px]"
      classNames={{
        content: '!p-0 overflow-hidden rounded-[10px]',
        closeButton: 'z-20 right-5 top-3 text-[16px] text-[#454655]',
      }}
      defaultOpen
      name="ReviewFlow"
      backdrop="blur"
      onClose={handleHerouiClose}
    >
      <div className="flex w-full items-stretch">
        <div
          className="relative hidden w-[310px] shrink-0 overflow-hidden md:block"
          style={{ backgroundColor: panelBg }}
        >
          <img
            src={REVIEW_PANEL_GRADIENT_BG}
            alt=""
            aria-hidden
            loading="eager"
            fetchPriority="high"
            decoding="async"
            className="absolute inset-0 h-full w-full object-cover"
          />

          <img
            src={REVIEW_ILLUSTRATION.entry.src}
            alt=""
            aria-hidden
            loading="eager"
            fetchPriority="high"
            decoding="async"
            className={clsx(
              layerCls,
              'h-full w-full object-cover',
              illustrationKey === 'entry' ? 'opacity-100' : 'opacity-0'
            )}
          />

          <img
            src={REVIEW_ILLUSTRATION.feedback.src}
            alt=""
            aria-hidden
            loading="eager"
            fetchPriority="high"
            decoding="async"
            className={clsx(
              layerCls,
              'h-full w-full object-cover',
              illustrationKey === 'feedback' ? 'opacity-100' : 'opacity-0'
            )}
          />

          <img
            src={NOT_GOOD_ILLUSTRATION.faces}
            alt=""
            aria-hidden
            loading="eager"
            fetchPriority="high"
            decoding="async"
            className={clsx(
              layerCls,
              'h-full w-full object-cover',
              illustrationKey === 'feedbackNotGood'
                ? 'opacity-100'
                : 'opacity-0'
            )}
          />

          <div
            className={clsx(
              layerCls,
              illustrationKey === 'trustpilot'
                ? 'opacity-100'
                : 'pointer-events-none opacity-0'
            )}
          >
            <img
              src={REVIEW_ILLUSTRATION.trustpilot.src}
              alt=""
              aria-hidden
              loading="eager"
              fetchPriority="high"
              decoding="async"
              className="absolute inset-0 h-full w-full object-cover"
            />
            <img
              src={THANKYOU_ILLUSTRATION.heart}
              alt=""
              aria-hidden
              loading="eager"
              fetchPriority="high"
              decoding="async"
              className={trustpilotHeartCls}
              style={{ willChange: 'transform' }}
            />
            <img
              src={THANKYOU_ILLUSTRATION.smile}
              alt=""
              aria-hidden
              loading="eager"
              fetchPriority="high"
              decoding="async"
              className={trustpilotSmileCls}
              style={{ willChange: 'transform' }}
            />
          </div>

          <div
            className={clsx(
              layerCls,
              step === 'thankyou'
                ? 'opacity-100'
                : 'pointer-events-none opacity-0'
            )}
          >
            <img
              src={THANKYOU_ILLUSTRATION.heart}
              alt=""
              aria-hidden
              loading="eager"
              fetchPriority="high"
              decoding="async"
              className={thankYouHeartCls}
              style={{ willChange: 'transform' }}
            />
            <img
              src={THANKYOU_ILLUSTRATION.smile}
              alt=""
              aria-hidden
              loading="eager"
              fetchPriority="high"
              decoding="async"
              className={thankYouSmileCls}
              style={{ willChange: 'transform' }}
            />
          </div>
        </div>

        <div className="relative flex min-h-[336px] flex-1 flex-col justify-center bg-white px-8 py-6">
          <div className="mx-auto w-full max-w-[355px]">
            {step === 'entry' && (
              <div className="flex flex-col gap-6">
                <h2 className={clsx(headingCls, 'text-left')}>
                  {REVIEW_COPY.entry.title(featureText)}
                </h2>
                <div className="flex flex-col gap-2">
                  {ENTRY_OPTIONS.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => handleSentiment(option.value)}
                      className="flex w-full items-center gap-3 rounded-[12px] bg-[#f5f5f5] p-4 text-left transition hover:bg-[#ededed]"
                    >
                      <span className="text-[24px] leading-none">
                        {option.emoji}
                      </span>
                      <span className="font-barlow text-[16px] font-medium text-black/[0.92]">
                        {option.label}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {step === 'trustpilot' && (
              <div className="flex h-[253px] flex-col justify-between">
                <div className="flex flex-col gap-2">
                  <h2 className={clsx(headingCls, 'text-center')}>
                    {REVIEW_COPY.trustpilot.title}
                  </h2>
                  <p className={subtitleCls}>
                    {REVIEW_COPY.trustpilot.subtitle}
                  </p>
                </div>
                <div className="flex flex-col gap-2">
                  <button
                    type="button"
                    className={primaryBtnCls}
                    onClick={handleLeaveReview}
                    disabled={awaitingReview}
                  >
                    {awaitingReview
                      ? REVIEW_COPY.trustpilot.waiting
                      : REVIEW_COPY.trustpilot.leaveReview}
                  </button>
                  <button
                    type="button"
                    className={secondaryBtnCls}
                    onClick={handleTrustpilotLater}
                  >
                    {REVIEW_COPY.trustpilot.later}
                  </button>
                </div>
              </div>
            )}

            {step === 'feedback' && (
              <div className="flex flex-col gap-2">
                <h2 className={clsx(headingCls, 'text-center')}>
                  {REVIEW_COPY.feedback.title}
                </h2>
                <p className={subtitleCls}>{REVIEW_COPY.feedback.subtitle}</p>
                <textarea
                  className="h-[94px] w-full resize-none rounded-[12px] border border-[#e9e9e9] p-4 font-barlow text-[15px] text-[#292a33] outline-none transition placeholder:text-[#6d6d6d] focus:border-[#4b7eff]"
                  placeholder={REVIEW_COPY.feedback.placeholder}
                  value={feedbackText}
                  onChange={(e) => setFeedbackText(e.target.value)}
                />
                <div className="flex flex-col gap-2">
                  <button
                    type="button"
                    className={primaryBtnCls}
                    onClick={handleShareFeedback}
                    disabled={!feedbackText.trim()}
                  >
                    {REVIEW_COPY.feedback.submit}
                  </button>
                  <button
                    type="button"
                    className={secondaryBtnCls}
                    onClick={handleSkipFeedback}
                  >
                    {REVIEW_COPY.feedback.skip}
                  </button>
                </div>
              </div>
            )}

            {step === 'thankyou' && (
              <div className="flex flex-col gap-9">
                <div className="flex flex-col gap-2">
                  <h2 className={clsx(headingCls, 'text-center')}>
                    {REVIEW_COPY.thankYou.title}
                  </h2>
                  <p className={subtitleCls}>{REVIEW_COPY.thankYou.subtitle}</p>
                </div>
                <button
                  type="button"
                  className={primaryBtnCls}
                  onClick={handleContinue}
                >
                  {REVIEW_COPY.thankYou.continue}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </NextUI.Modal>
  );
};

export default ReviewFlowModal;
